import {
  CheckCircleOutlined,
  DollarOutlined,
  PhoneOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { Button, Card, DatePicker, Select, Space, Tag, Typography } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { SalesMeetingStatus } from "@fitness-app/data-models/entities/SalesMeeting";

import { usePermissionRequirement } from "~/hooks/usePermission";
import { dashboardPermissionRequirements } from "~/modules/Dashboard/dashboardPermissionRequirements";
import { EmptyPanel } from "../components/EmptyPanel";
import { MetricCard } from "../components/MetricCard";
import { getRangePresets, meetingStatusLabels, statusColor } from "../salesCrmConstants";
import { formatMoney, getConfirmedSaleBundle, isWonMeeting } from "../salesCrmUtils";
import { type SalesMeetingRow } from "../hooks/useSalesMeetingsQuery";

const { RangePicker } = DatePicker;

type SellerOption = { value: string; label: string };

type SalesDashboardTabProps = {
  range: [Dayjs, Dayjs];
  setRange: (value: [Dayjs, Dayjs]) => void;
  canSeeAll: boolean;
  sellerOptions: SellerOption[];
  selectedSellerId: string | null;
  setSelectedSellerId: (value: string | null) => void;
  meetings: SalesMeetingRow[];
  loading: boolean;
  onSeeAllMeetings: () => void;
};

export const SalesDashboardTab = ({
  range,
  setRange,
  canSeeAll,
  sellerOptions,
  selectedSellerId,
  setSelectedSellerId,
  meetings,
  loading,
  onSeeAllMeetings,
}: SalesDashboardTabProps): React.ReactElement => {
  const navigate = useNavigate();
  const canManageCheckout = usePermissionRequirement(dashboardPermissionRequirements.salesCheckout);
  const kpi = useMemo(() => {
    const calls = meetings.filter((meeting) => meeting.status !== SalesMeetingStatus.Rescheduled).length;
    const attended = meetings.filter((meeting) => meeting.status === SalesMeetingStatus.Attended).length;
    const wonMeetings = meetings.filter(isWonMeeting);
    const sold = wonMeetings.length;
    const paid = meetings.reduce((sum, meeting) => sum + (getConfirmedSaleBundle(meeting)?.amount ?? 0), 0);
    const contracted = meetings.reduce((sum, meeting) => {
      const bundle = getConfirmedSaleBundle(meeting);
      return sum + (bundle?.totalAmount ?? bundle?.amount ?? 0);
    }, 0);
    const scheduled = meetings.filter((meeting) => meeting.status === SalesMeetingStatus.Scheduled).length;
    const canceled = meetings.filter((meeting) => meeting.status === SalesMeetingStatus.Canceled).length;
    const noShow = meetings.filter((meeting) => meeting.status === SalesMeetingStatus.NoShow).length;
    const rescheduled = meetings.filter((meeting) => meeting.status === SalesMeetingStatus.Rescheduled).length;

    return {
      calls,
      attended,
      sold,
      paid,
      contracted,
      scheduled,
      canceled,
      noShow,
      rescheduled,
      attendanceRate: calls ? Math.round((attended / calls) * 100) : 0,
      closeRate: attended ? Math.round((sold / attended) * 100) : 0,
    };
  }, [meetings]);

  const funnelRows = [
    { label: "Zaplanowane", value: kpi.scheduled, color: "bg-blue-500" },
    { label: "Odbyte", value: kpi.attended, color: "bg-emerald-500" },
    { label: "Nieobecne", value: kpi.noShow, color: "bg-red-500" },
    { label: "Odwołane", value: kpi.canceled, color: "bg-gray-400" },
    { label: "Przełożone", value: kpi.rescheduled, color: "bg-amber-500" },
  ];
  const maxFunnelValue = Math.max(1, ...funnelRows.map((row) => row.value));
  const recentMeetings = meetings.slice(0, 5);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Sales CRM
          </Typography.Text>
          <Typography.Title level={3} className="!mb-0 !mt-1 !text-2xl !font-semibold !tracking-normal">
            Konsultacje i sprzedaż
          </Typography.Title>
          <Typography.Text className="text-gray-500">
            Calendly jako źródło prawdy, feedback rozmów i dopięcie sprzedaży w jednym miejscu.
          </Typography.Text>
        </div>
        <Space wrap align="center">
          <RangePicker
            value={range}
            presets={getRangePresets()}
            onChange={(value) => value?.[0] && value?.[1] && setRange([value[0], value[1]])}
          />
          {canSeeAll ? (
            <Select
              allowClear
              placeholder="Sprzedawca"
              value={selectedSellerId}
              onChange={(value) => setSelectedSellerId(value ?? null)}
              options={sellerOptions}
              style={{ width: 240 }}
            />
          ) : null}
          {canManageCheckout ? (
            <Button icon={<ShoppingCartOutlined />} onClick={() => navigate("/sales/checkout-variants")}>
              Warianty koszyka
            </Button>
          ) : null}
        </Space>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          title="Rozmowy"
          value={kpi.calls}
          helper={`${kpi.scheduled} zaplanowanych, ${kpi.rescheduled} przełożonych`}
          icon={<PhoneOutlined className="text-base text-blue-700" />}
          accent="bg-blue-50"
          loading={loading}
        />
        <MetricCard
          title="Obecność"
          value={`${kpi.attended} / ${kpi.attendanceRate}%`}
          helper={`${kpi.noShow} nieobecne w wybranym okresie`}
          icon={<CheckCircleOutlined className="text-base text-emerald-700" />}
          accent="bg-emerald-50"
          loading={loading}
        />
        <MetricCard
          title="Sprzedaże"
          value={`${kpi.sold} / ${kpi.closeRate}%`}
          helper="Program Hollywood, konwersja liczona z odbytych rozmów"
          icon={<TeamOutlined className="text-base text-violet-700" />}
          accent="bg-violet-50"
          loading={loading}
        />
        <MetricCard
          title="Płatność"
          value={formatMoney(kpi.paid)}
          helper={`Zakontraktowane: ${formatMoney(kpi.contracted)}`}
          icon={<DollarOutlined className="text-base text-amber-700" />}
          accent="bg-amber-50"
          loading={loading}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.35fr_0.65fr]">
        <Card
          loading={loading}
          styles={{ body: { padding: 20 } }}
          className="rounded-lg border border-gray-200 shadow-sm"
        >
          <div className="mb-5 flex items-center justify-between gap-3">
            <div>
              <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Lejek spotkań
              </Typography.Text>
              <Typography.Title level={4} className="!mb-0 !mt-1 !text-lg !font-semibold">
                Statusy rozmów w okresie
              </Typography.Title>
            </div>
            <Tag color={kpi.attendanceRate >= 70 ? "green" : "gold"}>Obecność {kpi.attendanceRate}%</Tag>
          </div>
          {meetings.length ? (
            <div className="space-y-4">
              {funnelRows.map((row) => (
                <div key={row.label} className="grid grid-cols-[120px_1fr_48px] items-center gap-3">
                  <Typography.Text className="text-sm text-gray-600">{row.label}</Typography.Text>
                  <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className={`h-full rounded-full ${row.color}`}
                      style={{
                        width: row.value > 0 ? `${Math.max(6, (row.value / maxFunnelValue) * 100)}%` : 0,
                      }}
                    />
                  </div>
                  <Typography.Text className="text-right font-medium text-gray-900">{row.value}</Typography.Text>
                </div>
              ))}
            </div>
          ) : (
            <EmptyPanel
              title="Brak spotkań w wybranym zakresie"
              description="Po wpięciu Calendly i migracji sales_meeting pojawią się tutaj statusy rozmów."
            />
          )}
        </Card>

        <Card
          loading={loading}
          styles={{ body: { padding: 20 } }}
          className="rounded-lg border border-gray-200 shadow-sm"
        >
          <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Płatność
          </Typography.Text>
          <Typography.Title level={2} className="!mb-1 !mt-2 !font-semibold !tracking-normal">
            {formatMoney(kpi.paid)}
          </Typography.Title>
          <Typography.Text className="text-gray-500">Pierwsza opłacona kwota z koszyka</Typography.Text>

          <div className="mt-6 space-y-3">
            <div className="flex items-center justify-between">
              <Typography.Text className="text-gray-500">Zakontraktowane</Typography.Text>
              <Typography.Text className="font-medium">{formatMoney(kpi.contracted)}</Typography.Text>
            </div>
            <div className="flex items-center justify-between">
              <Typography.Text className="text-gray-500">Wygrane rozmowy</Typography.Text>
              <Typography.Text className="font-medium">{kpi.sold}</Typography.Text>
            </div>
            <div className="flex items-center justify-between">
              <Typography.Text className="text-gray-500">Konwersja sprzedaży</Typography.Text>
              <Typography.Text className="font-medium">{kpi.closeRate}%</Typography.Text>
            </div>
          </div>
        </Card>
      </div>

      <Card
        loading={loading}
        styles={{ body: { padding: 20 } }}
        className="rounded-lg border border-gray-200 shadow-sm"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Typography.Text className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Ostatnie spotkania
            </Typography.Text>
            <Typography.Title level={4} className="!mb-0 !mt-1 !text-lg !font-semibold">
              Najnowsze rozmowy sprzedażowe
            </Typography.Title>
          </div>
          <Button onClick={onSeeAllMeetings}>Zobacz wszystkie</Button>
        </div>
        {recentMeetings.length ? (
          <div className="divide-y divide-gray-100">
            {recentMeetings.map((meeting) => (
              <div key={meeting.id} className="grid gap-3 py-3 md:grid-cols-[1fr_160px_120px_120px] md:items-center">
                <div className="min-w-0">
                  <Typography.Text className="block truncate font-medium text-gray-950">
                    {meeting.clientName ?? meeting.clientEmail}
                  </Typography.Text>
                  <Typography.Text className="text-sm text-gray-500">{meeting.clientEmail}</Typography.Text>
                </div>
                <Typography.Text className="text-sm text-gray-600">
                  <CalendarOutlined /> {dayjs(meeting.startTime).format("DD.MM HH:mm")}
                </Typography.Text>
                <Tag color={statusColor[meeting.status] ?? "default"} className="w-fit">
                  {meetingStatusLabels[meeting.status]}
                </Tag>
                <Typography.Text className="text-sm font-medium">
                  {formatMoney(getConfirmedSaleBundle(meeting)?.amount, getConfirmedSaleBundle(meeting)?.currency)}
                </Typography.Text>
              </div>
            ))}
          </div>
        ) : (
          <EmptyPanel
            title="Brak spotkań do pokazania"
            description="Tabela wypełni się automatycznie po pierwszych eventach Calendly."
          />
        )}
      </Card>
    </div>
  );
};
